import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EditNavButton, BrandingEditingProvider, EditableText, EditablePhoto, useFieldEditing, useInlineEdit } from '@kevlarcon/pages';
import { Arrow } from './icons';
import { applyBrandingSave, siteApi, MenuItem, SiteBranding } from '../api/site';
import { authApi, TenantBranding } from '../api/auth';
import { applyOrgTheme } from '../theme/applyOrgTheme';
import { useBrandPreview } from '../contexts/BrandPreviewContext';

type Props = { active?: string; onEditNav?: () => void };

// Read by @kevlarcon/pages' EditableSection so its floating edit controls
// stick below this header instead of scrolling in underneath it.
const HEADER_HEIGHT_VAR = '--pages-header-height';

const TITLE_WIDTH_MIN = 60;
const TITLE_WIDTH_MAX = 600;

/**
 * The site-name wordmark plus its edit-mode line-wrap handle. Dragging sets
 * `title_max_width` — a `<span>` is inline by default and only wraps once
 * given a box to wrap inside, so `baseStyle` (passed down from `SiteHeader`,
 * everything except max-width) gains `display: inline-block` the moment a
 * width is in play, whether committed or mid-drag. Drag state
 * (`dragWidth`) tracks visually in real time; the server commit only happens
 * once, on release, rather than on every pointer-move.
 */
const TitleWithWidthHandle: React.FC<{
  value: string;
  baseStyle: React.CSSProperties;
  committedWidth: number | null;
}> = ({ value, baseStyle, committedWidth }) => {
  const { patchField } = useFieldEditing();
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  // Measures the wrapper, not the handle — it shrinks to fit the title text
  // when no max-width is set yet, so its width is the title's natural size,
  // which is what the very first drag (before any committed width) should
  // start from.
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  const effectiveWidth = dragWidth ?? committedWidth;

  const onPointerDown = (e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault();
    const startWidth = committedWidth ?? wrapperRef.current?.getBoundingClientRect().width ?? 200;
    dragStart.current = { x: e.clientX, width: startWidth };
    setDragWidth(startWidth);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!dragStart.current) return;
    const next = Math.min(TITLE_WIDTH_MAX, Math.max(TITLE_WIDTH_MIN, Math.round(dragStart.current.width + (e.clientX - dragStart.current.x))));
    setDragWidth(next);
  };

  const onPointerUp = () => {
    if (!dragStart.current) return;
    patchField(['title_max_width'], dragWidth ?? dragStart.current.width);
    dragStart.current = null;
  };

  const style: React.CSSProperties = {
    ...baseStyle,
    ...(effectiveWidth ? { maxWidth: `${effectiveWidth}px`, display: 'inline-block', whiteSpace: 'normal' } : {}),
  };

  return (
    <span ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <EditableText value={value} path={['title']} as="span" className="brand-title" style={style} />
      <span
        data-testid="site-header-brand-title-width-handle"
        role="separator"
        aria-orientation="vertical"
        title="Drag to control line wrapping · double-click to reset"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onDoubleClick={() => patchField(['title_max_width'], null)}
        className="brand-title-drag-handle"
      />
    </span>
  );
};

/**
 * The tenant site header. The nav is entirely CMS-driven: every visible link,
 * including the built-in Apply/Give CTAs, is a `MenuItem` row (seeded per
 * organization — see tenant_site.seeding.seed_site_for_organization) drawn from
 * one ordered list, not a mix of CMS links plus hardcoded JSX. Apply/Give are
 * seeded `is_locked` so their text/url can't be edited or deleted, but they're
 * still ordinary members of the list an org admin can reorder or toggle off —
 * `css_class` (a `btn ...` class) is what routes an item into the CTA slot
 * instead of the plain-link nav, not a hardcoded url/text match. The logo and
 * title come from the tenant's branding (name/logo), not a hardcoded string,
 * so this component renders correctly for any tenant. They're editable in
 * place for an org admin — see `pages.branding` and
 * `tenant_site.branding_provider` — reusing the same `EditablePhoto`/
 * `EditableText` primitives django-pages' section editor uses, wrapped in a
 * `BrandingEditingProvider` instead of a `SectionEditingProvider` since these
 * fields aren't part of any `Section`.
 */
export default function SiteHeader({ active, onEditNav }: Props) {
  const queryClient = useQueryClient();
  const { isEditMode } = useInlineEdit();

  const { data: menu } = useQuery<MenuItem[]>({
    queryKey: ['site-menu', 'main'],
    queryFn: () => siteApi.getMenu('main'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: branding } = useQuery<TenantBranding>({
    queryKey: ['tenant_branding'],
    queryFn: authApi.getBranding,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // BrandStyleModal is a sibling, not a parent, so its sliders reach this
  // render via BrandPreviewContext rather than props — see that context's
  // doc comment. Falls back to the saved value whenever the modal isn't open
  // (or hasn't touched a given field).
  const preview = useBrandPreview();

  // Seeded from the same branding the public site already reads (see
  // OrganizationBrandingSerializer, which sources `name`/`logo`/`logo_alt`
  // from this same provider) — no second fetch needed just to edit it.
  const brandingValues: SiteBranding = {
    title: branding?.name ?? '',
    logo: branding?.logo ?? '',
    logo_alt: branding?.logo_alt ?? '',
    header_logo_height: preview.header_logo_height !== undefined ? preview.header_logo_height : (branding?.header_logo_height ?? null),
    footer_logo: branding?.footer_logo ?? '',
    footer_logo_alt: branding?.footer_logo_alt ?? '',
    footer_logo_height: preview.footer_logo_height !== undefined ? preview.footer_logo_height : (branding?.footer_logo_height ?? null),
    title_font_size: preview.title_font_size !== undefined ? preview.title_font_size : (branding?.title_font_size ?? null),
    title_text_transform: preview.title_text_transform !== undefined ? preview.title_text_transform : (branding?.title_text_transform ?? ''),
    title_bold: preview.title_bold !== undefined ? preview.title_bold : (branding?.title_bold ?? false),
    title_italic: preview.title_italic !== undefined ? preview.title_italic : (branding?.title_italic ?? false),
    title_max_width: branding?.title_max_width ?? null,
    footer_heading: branding?.footer_heading ?? '',
    footer_tagline: branding?.footer_tagline ?? '',
    footer_doxology: branding?.footer_doxology ?? '',
  };

  // Max-width is handled separately by TitleWithWidthHandle (edit mode) /
  // applied directly below (static render) — everything else is shared.
  const titleStyle: React.CSSProperties = {
    ...(brandingValues.title_font_size ? { fontSize: `${brandingValues.title_font_size}px` } : {}),
    ...(brandingValues.title_text_transform ? { textTransform: brandingValues.title_text_transform } : {}),
    ...(brandingValues.title_bold ? { fontWeight: 700 } : {}),
    ...(brandingValues.title_italic ? { fontStyle: 'italic' } : {}),
  };
  const staticTitleStyle: React.CSSProperties = {
    ...titleStyle,
    ...(brandingValues.title_max_width
      ? { maxWidth: `${brandingValues.title_max_width}px`, display: 'inline-block', whiteSpace: 'normal' }
      : {}),
  };

  // SiteHeader renders once per TenantSite, so this is where the org's saved
  // theme gets applied to the live page — re-runs whenever the theme drawer's
  // save invalidates this query.
  useEffect(() => {
    if (branding) applyOrgTheme(branding);
  }, [branding]);

  const headerRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const updateHeight = () => {
      document.documentElement.style.setProperty(HEADER_HEIGHT_VAR, `${el.getBoundingClientRect().height}px`);
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [menu, branding]);

  // Apply/Give are ordinary menu items styled as buttons (see the component
  // doc comment) — a `btn` css_class is what puts an item in the CTA slot,
  // not a hardcoded url/text match.
  const items = menu ?? [];
  const linkItems = items.filter((item) => !item.css_class?.includes('btn'));
  const ctaItems = items.filter((item) => item.css_class?.includes('btn'));

  // Edit mode swaps the brand block from a navigating <a> to a plain <div> —
  // EditablePhoto's click-to-open and EditableText's contentEditable focus
  // shouldn't also navigate to "/" out from under an admin trying to edit.
  const BrandTag = isEditMode ? 'div' : 'a';
  const brandProps = isEditMode ? {} : { href: '/' };

  return (
    <header className="site-header" ref={headerRef}>
      <div className="wrap nav-row">
        <BrandTag className="brand" {...brandProps}>
          {isEditMode ? (
            <BrandingEditingProvider
              endpoint="/site/branding/"
              values={brandingValues}
              active
              onSaved={(saved) => queryClient.setQueryData<TenantBranding>(
                ['tenant_branding'],
                (old) => (old ? applyBrandingSave(old, saved) : old),
              )}
            >
              <EditablePhoto
                src={brandingValues.logo}
                alt={brandingValues.logo_alt}
                srcPath={['logo']}
                altPath={['logo_alt']}
                maxDimensions={{ width: 800, height: 800 }}
                imgProps={{
                  className: `brand-logo${brandingValues.logo ? '' : ' brand-logo-empty'}`,
                  style: brandingValues.header_logo_height ? { height: `${brandingValues.header_logo_height}px` } : undefined,
                }}
              />
              <TitleWithWidthHandle
                value={brandingValues.title}
                baseStyle={titleStyle}
                committedWidth={brandingValues.title_max_width}
              />
            </BrandingEditingProvider>
          ) : (
            <>
              {brandingValues.logo && (
                <img
                  src={brandingValues.logo}
                  alt={brandingValues.logo_alt}
                  className="brand-logo"
                  style={brandingValues.header_logo_height ? { height: `${brandingValues.header_logo_height}px` } : undefined}
                />
              )}
              <span className="brand-title" style={staticTitleStyle}>{brandingValues.title}</span>
            </>
          )}
        </BrandTag>
        <nav className="nav-main">
          {linkItems.map((item) => (
            <a
              key={item.id}
              href={item.url}
              className={`${item.css_class ?? ''}${active && item.text.toLowerCase() === active ? ' active' : ''}`.trim()}
              data-testid={`site-nav-${item.id}-link`}
            >
              {item.text}
            </a>
          ))}
        </nav>
        <div className="nav-cta">
          {ctaItems.map((item) => (
            <a key={item.id} href={item.url} className={item.css_class} data-testid={`site-nav-${item.id}-link`}>
              {item.text}
              {item.css_class?.includes('btn-primary') && <Arrow />}
            </a>
          ))}
          {onEditNav && <EditNavButton onClick={onEditNav} />}
        </div>
      </div>
      <div className="ribbon-accents" aria-hidden="true">
        <span className="r1"></span><span className="r2"></span>
        <span className="r3"></span><span className="r4"></span><span className="r5"></span>
      </div>
    </header>
  );
}
